import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../utils/api'
import { UserSecurityBadges } from '../components/UserSecurityBadges'
import { CustomersIcon, TasksIcon, DollarIcon } from '../components/Icons'

interface MembershipTier {
  id: number
  name: string
  price: number
  is_active: boolean
}

interface AdminUser {
  id: number
  first_name: string
  last_name: string
  email: string
  phone?: string
  language: string
  role: 'admin' | 'broker'
  created_at: string
  updated_at: string
  tier_id?: number | null
  tier_name?: string
  tier_price?: number
  stripe_customer_id?: string
  stripe_subscription_id?: string
  subscription_status?: string
  subscription_current_period_end?: string
  subscription_canceled_at?: string
  trial_ends_at?: string
  trial_used?: boolean
  subscription_override_until?: string | null
  subscription_override_reason?: string | null
  subscription_discount_percent?: number | null
  stats: { tasks: number; deals: number; customers: number }
  recentActivity: Array<{ description: string; due_date: string; status: string; created_at: string }>
}

const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm'
const LABEL = 'block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-5">{title}</h2>
      {children}
    </div>
  )
}

function StatusBadge({ status }: { status?: string | null }) {
  const map: Record<string, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    past_due: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    canceled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  }
  const key = status || 'inactive'
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${map[key] ?? map.inactive}`}>
      {key}
    </span>
  )
}

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAdmin } = useAuth()

  const [user, setUser] = useState<AdminUser | null>(null)
  const [tiers, setTiers] = useState<MembershipTier[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [overrideSaving, setOverrideSaving] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Profile form state
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', phone: '', language: 'en', role: 'broker' as 'admin' | 'broker', tier_id: '' })

  // Override form state
  const [override, setOverride] = useState({ tier_id: '', override_until: '', override_reason: '', discount_percent: '' })
  const [overrideMode, setOverrideMode] = useState<'access' | 'discount' | 'clear'>('access')

  const token = () => localStorage.getItem('token') || ''
  const authHeaders = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' })

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, { headers: { Authorization: `Bearer ${token()}` } })
      if (!res.ok) { navigate('/dashboard/users'); return }
      const data = await res.json()
      const u: AdminUser = data.user
      setUser(u)
      setProfile({
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
        phone: u.phone || '',
        language: u.language,
        role: u.role,
        tier_id: u.tier_id ? String(u.tier_id) : '',
      })
      setOverride({
        tier_id: u.tier_id ? String(u.tier_id) : '',
        override_until: u.subscription_override_until
          ? new Date(u.subscription_override_until).toISOString().slice(0, 10)
          : '',
        override_reason: u.subscription_override_reason || '',
        discount_percent: u.subscription_discount_percent != null ? String(u.subscription_discount_percent) : '',
      })
    } catch { showToast('Failed to load user', 'error') }
    finally { setLoading(false) }
  }, [id])

  const fetchTiers = useCallback(async () => {
    const res = await fetch(`${API_BASE_URL}/api/memberships?include_inactive=true`, { headers: { Authorization: `Bearer ${token()}` } })
    if (res.ok) { const d = await res.json(); setTiers(d.tiers || []) }
  }, [])

  useEffect(() => {
    if (isAdmin) { fetchUser(); fetchTiers() }
  }, [isAdmin, fetchUser, fetchTiers])

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const body: any = { ...profile, tier_id: profile.tier_id === '' ? null : parseInt(profile.tier_id) }
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Update failed')
      showToast('Profile updated successfully')
      await fetchUser()
    } catch (err: any) {
      showToast(err.message || 'Failed to update profile', 'error')
    } finally { setSaving(false) }
  }

  const handleSync = async () => {
    setSyncLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/sync-user-subscription/${id}`, {
        method: 'POST',
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error('Sync failed')
      showToast('Subscription synced from Stripe')
      await fetchUser()
    } catch (err: any) {
      showToast(err.message || 'Sync failed', 'error')
    } finally { setSyncLoading(false) }
  }

  const handleApplyOverride = async (e: React.FormEvent) => {
    e.preventDefault()
    setOverrideSaving(true)
    try {
      let body: any = {}
      if (overrideMode === 'clear') {
        body = { tier_id: null, override_until: null, override_reason: null, discount_percent: null }
      } else if (overrideMode === 'access') {
        body = {
          tier_id: override.tier_id ? parseInt(override.tier_id) : undefined,
          override_until: override.override_until || null,
          override_reason: override.override_reason || null,
          discount_percent: null,
        }
      } else {
        body = {
          discount_percent: override.discount_percent ? parseInt(override.discount_percent) : null,
          override_until: override.override_until || null,
          override_reason: override.override_reason || null,
        }
      }
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${id}/subscription-override`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Override failed')
      showToast('Subscription override applied')
      await fetchUser()
    } catch (err: any) {
      showToast(err.message || 'Failed to apply override', 'error')
    } finally { setOverrideSaving(false) }
  }

  const handleImpersonate = async () => {
    const res = await fetch(`${API_BASE_URL}/api/admin/impersonate/${id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token()}` },
    })
    if (res.ok) {
      const data = await res.json()
      sessionStorage.setItem('original_token', token())
      localStorage.setItem('token', data.token)
      sessionStorage.setItem('is_impersonating', 'true')
      window.location.href = '/dashboard'
    } else {
      showToast('Failed to impersonate user', 'error')
    }
  }

  const handleDelete = async () => {
    setDeleteLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      })
      if (!res.ok) throw new Error('Delete failed')
      navigate('/dashboard/users')
    } catch (err: any) {
      showToast(err.message || 'Failed to delete user', 'error')
    } finally { setDeleteLoading(false) }
  }

  const fmt = (iso?: string | null) => {
    if (!iso) return '—'
    const d = new Date(iso)
    if (d.getFullYear() >= 2099) return 'Indefinite'
    return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
  }
  const hasActiveOverride = user?.subscription_override_until
    && new Date(user.subscription_override_until) > new Date()

  if (!isAdmin) return <div className="p-8 text-center text-gray-500">Access denied</div>
  if (loading) return <div className="flex items-center justify-center min-h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white" /></div>
  if (!user) return <div className="p-8 text-center text-gray-500">User not found</div>

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${toast.type === 'success' ? 'bg-gray-900' : 'bg-red-600'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/dashboard/users')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <div className="w-14 h-14 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-xl">{user.first_name[0]}{user.last_name[0]}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{user.first_name} {user.last_name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'}`}>
              {user.role}
            </span>
            <StatusBadge status={user.subscription_status} />
            {hasActiveOverride && (
              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                Override active
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={handleImpersonate} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
            Impersonate
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Customers', value: user.stats.customers, Icon: CustomersIcon, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
          { label: 'Deals', value: user.stats.deals, Icon: DollarIcon, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
          { label: 'Tasks', value: user.stats.tasks, Icon: TasksIcon, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20' },
        ].map(({ label, value, Icon, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-5 text-center`}>
            <Icon className={`h-7 w-7 ${color} mx-auto mb-2`} />
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Profile */}
        <Section title="Profile">
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>First Name</label>
                <input className={INPUT} value={profile.first_name} onChange={e => setProfile(p => ({ ...p, first_name: e.target.value }))} required />
              </div>
              <div>
                <label className={LABEL}>Last Name</label>
                <input className={INPUT} value={profile.last_name} onChange={e => setProfile(p => ({ ...p, last_name: e.target.value }))} required />
              </div>
            </div>
            <div>
              <label className={LABEL}>Email</label>
              <input type="email" className={INPUT} value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} required />
            </div>
            <div>
              <label className={LABEL}>Phone</label>
              <input type="tel" className={INPUT} value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Language</label>
                <select className={INPUT} value={profile.language} onChange={e => setProfile(p => ({ ...p, language: e.target.value }))}>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </div>
              <div>
                <label className={LABEL}>Role</label>
                <select className={INPUT} value={profile.role} onChange={e => setProfile(p => ({ ...p, role: e.target.value as any }))}>
                  <option value="broker">Broker</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div>
              <label className={LABEL}>Base Membership Tier</label>
              <select className={INPUT} value={profile.tier_id} onChange={e => setProfile(p => ({ ...p, tier_id: e.target.value }))}>
                <option value="">No Tier (Inactive)</option>
                {tiers.map(t => (
                  <option key={t.id} value={t.id}>{t.name} — CA${t.price}/mo{!t.is_active ? ' (inactive)' : ''}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Sets tier directly, bypassing Stripe.</p>
            </div>
            <button type="submit" disabled={saving} className="w-full py-2.5 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
          </form>
        </Section>

        {/* Subscription */}
        <div className="space-y-4">
          <Section title="Subscription">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-500 dark:text-gray-400">Plan</span>
                <span className="font-medium text-gray-900 dark:text-white">{user.tier_name || '—'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-500 dark:text-gray-400">Status</span>
                <StatusBadge status={user.subscription_status} />
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-500 dark:text-gray-400">Period ends</span>
                <span className="font-medium text-gray-900 dark:text-white">{fmt(user.subscription_current_period_end)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-500 dark:text-gray-400">Trial ends</span>
                <span className="font-medium text-gray-900 dark:text-white">{fmt(user.trial_ends_at)}</span>
              </div>
              {user.subscription_override_until && (
                <div className={`flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 ${hasActiveOverride ? 'text-amber-700 dark:text-amber-400' : 'text-gray-400'}`}>
                  <span>Override until</span>
                  <span className="font-medium">{fmt(user.subscription_override_until)}</span>
                </div>
              )}
              {user.subscription_discount_percent != null && (
                <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400">Discount</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{user.subscription_discount_percent}% off</span>
                </div>
              )}
              {user.subscription_override_reason && (
                <div className="py-2 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">Override reason</span>
                  <span className="text-gray-700 dark:text-gray-300 text-xs mt-0.5 block">{user.subscription_override_reason}</span>
                </div>
              )}
              <div className="flex justify-between pt-1">
                <span className="text-gray-500 dark:text-gray-400">Stripe Customer</span>
                <span className="font-mono text-xs text-gray-600 dark:text-gray-400 truncate max-w-[180px]">{user.stripe_customer_id || '—'}</span>
              </div>
            </div>
            <button onClick={handleSync} disabled={syncLoading} className="mt-4 w-full py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
              {syncLoading ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              )}
              Sync from Stripe
            </button>
          </Section>

          {/* Subscription Override */}
          <Section title="Subscription Override">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Grant flexible access independently of Stripe: free tier access, discounts, or time-limited access for partners, beta testers, etc.
            </p>
            <div className="flex gap-2 mb-4">
              {(['access', 'discount', 'clear'] as const).map(m => (
                <button key={m} onClick={() => setOverrideMode(m)} className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors capitalize ${overrideMode === m ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                  {m === 'access' ? 'Grant Access' : m === 'discount' ? 'Discount' : 'Clear Override'}
                </button>
              ))}
            </div>

            {overrideMode === 'clear' ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">This will remove the override tier, expiry date, and discount. The user's Stripe subscription status will remain unchanged.</p>
                <button onClick={handleApplyOverride} disabled={overrideSaving} className="w-full py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                  {overrideSaving ? 'Clearing…' : 'Clear Override'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleApplyOverride} className="space-y-3">
                {overrideMode === 'access' && (
                  <div>
                    <label className={LABEL}>Grant Tier</label>
                    <select className={INPUT} value={override.tier_id} onChange={e => setOverride(o => ({ ...o, tier_id: e.target.value }))}>
                      <option value="">Keep current tier</option>
                      {tiers.map(t => (
                        <option key={t.id} value={t.id}>{t.name} — CA${t.price}/mo</option>
                      ))}
                    </select>
                  </div>
                )}
                {overrideMode === 'discount' && (
                  <div>
                    <label className={LABEL}>Discount %</label>
                    <div className="relative">
                      <input type="number" min={0} max={100} className={INPUT} placeholder="e.g. 50" value={override.discount_percent} onChange={e => setOverride(o => ({ ...o, discount_percent: e.target.value }))} />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Informational — apply the matching coupon in Stripe. This records the intent.</p>
                  </div>
                )}
                <div>
                  <label className={LABEL}>Expires on <span className="text-green-600 dark:text-green-400 normal-case font-normal">(leave blank = no expiry)</span></label>
                  <input type="date" className={INPUT} value={override.override_until} onChange={e => setOverride(o => ({ ...o, override_until: e.target.value }))} />
                </div>
                <div>
                  <label className={LABEL}>Reason (internal note)</label>
                  <input type="text" className={INPUT} placeholder="e.g. Partner deal, beta tester, sponsorship…" value={override.override_reason} onChange={e => setOverride(o => ({ ...o, override_reason: e.target.value }))} />
                </div>
                <button type="submit" disabled={overrideSaving} className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                  {overrideSaving ? 'Applying…' : overrideMode === 'access' ? 'Grant Access' : 'Apply Discount'}
                </button>
              </form>
            )}
          </Section>
        </div>
      </div>

      {/* Security */}
      <Section title="Security">
        <UserSecurityBadges userId={user.id} onSecurityChanged={fetchUser} />
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Joined</span>
            <p className="font-medium text-gray-900 dark:text-white">{fmt(user.created_at)}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Last updated</span>
            <p className="font-medium text-gray-900 dark:text-white">{fmt(user.updated_at)}</p>
          </div>
        </div>
      </Section>

      {/* Recent Activity */}
      <Section title="Recent Activity">
        {user.recentActivity.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-sm text-center py-4">No recent activity</p>
        ) : (
          <div className="space-y-2">
            {user.recentActivity.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{a.description}</p>
                  {a.due_date && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Due {fmt(a.due_date)}</p>}
                </div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  a.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : a.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}>{a.status}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Danger Zone */}
      <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-6">
        <h2 className="text-base font-semibold text-red-700 dark:text-red-400 mb-3">Danger Zone</h2>
        {!showDeleteConfirm ? (
          <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors">
            Delete User
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-red-700 dark:text-red-400">
              This will permanently delete <strong>{user.first_name} {user.last_name}</strong> and all their data (customers, deals, tasks, conversations). This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleteLoading} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors">
                {deleteLoading ? 'Deleting…' : 'Yes, delete permanently'}
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

export default AdminUserDetail
